/**
 * Shape of an error body returned by the accounting backend when a GL write
 * or seed-balance operation is rejected.
 *
 * All fields are optional because different backend endpoints populate
 * different subsets of them.
 */
export interface IBackendErrorBody {
  message?: string;
  reason?: string;
  period_closed?: boolean;
  retained_earnings_blocked?: boolean;
  errors?: Array<{ error?: string; period_closed?: boolean; reason?: string }>;
}

const PERIOD_CLOSED_MESSAGE =
  "This period is closed. Ask an admin to reopen the earliest affected period, or post the entry in the current open period.";

const RETAINED_EARNINGS_BLOCKED_MESSAGE =
  "Year-end retained earnings is automatic. If this is a prior-period audit adjustment, resubmit with the 'Prior-period adjustment' checkbox.";

const FISCAL_YEAR_CLOSED_MESSAGE =
  "This fiscal year is locked. Reopen from period 12 first.";

const NO_PERIOD_MESSAGE =
  "No fiscal period covers this date. Check the fiscal calendar.";

const DEFAULT_MESSAGE = "An error occurred";

/**
 * Translates a raw backend error body into a friendly, user-facing message.
 *
 * The rule table is applied in priority order:
 *  1. Closed period (`period_closed`, nested `errors[].period_closed`, or
 *     `reason === "PERIOD_CLOSED"`).
 *  2. Retained-earnings block (`retained_earnings_blocked`).
 *  3. Fiscal year locked (`reason === "FISCAL_YEAR_CLOSED"`).
 *  4. No fiscal period covers the date (`reason === "NO_PERIOD"`).
 *  5. Fallback to the backend `message`, or a generic default.
 *
 * @param body - The backend error body (may be undefined).
 * @returns A friendly message suitable for display via `showMessage`.
 */
export function translateBackendError(
  body: IBackendErrorBody | undefined
): string {
  const isPeriodClosed =
    Boolean(body?.period_closed) ||
    body?.reason === "PERIOD_CLOSED" ||
    Boolean(
      body?.errors?.some(
        (entry) =>
          Boolean(entry?.period_closed) || entry?.reason === "PERIOD_CLOSED"
      )
    );

  if (isPeriodClosed) {
    return PERIOD_CLOSED_MESSAGE;
  }

  if (body?.retained_earnings_blocked) {
    return RETAINED_EARNINGS_BLOCKED_MESSAGE;
  }

  if (body?.reason === "FISCAL_YEAR_CLOSED") {
    return FISCAL_YEAR_CLOSED_MESSAGE;
  }

  if (body?.reason === "NO_PERIOD") {
    return NO_PERIOD_MESSAGE;
  }

  return body?.message ?? DEFAULT_MESSAGE;
}
